import { of as observableOf, Observable } from 'rxjs';
import { Injectable } from '@angular/core';

import { HisBackendService, HisUploadFile } from 'hisng';

import { Project, SimpleProject } from './../../model/project/project';

@Injectable()
export class ProjectService {

    constructor( private backendService: HisBackendService ) { }

    /**
     * getProjectById service call to load project with Id.
     *
     * @param id Id of project to load.
     */
    getProjectById( id: number ): Observable<Project> {
        return this.backendService.getData( `api/v1/fs/res/project/${id}` );
    }

    /**
     * get project with details (includes all available listview content)
     *
     * @param id - id of the project
     */
    getProjectWithDetailsById( id: number ): Observable<Project> {
        return this.backendService.getData( `api/v1/fs/res/project/${id}/details` );
    }

    // create/0 should generate a new projectId (0) as temporary identifier for new project
    getNewProject(): Observable<Project> {
        return observableOf( new Project() );
    }

    /**
     * save new or existing project data.
     *
     * @param project Project to save.
     */
    saveProject( project: Project ): Observable<Project> {
        if ( project.id == null ) {
            return this.backendService.postData( 'api/v1/fs/res/project', project );
        } else {
            return this.backendService.putData( `api/v1/fs/res/project/${project.id}`, project );
        }
    }

    /**
     * delete project from database.
     *
     * @param projectId Id of project to delete.
     * @return Observable.of( 0=fail / 1=success )
     */
    deleteProject( projectId: number ): Observable<number> {
        return this.backendService.delete( `api/v1/fs/res/project/${projectId}` );
    }

    /**
     * check if project can be deleted and has not further references to other objects.
     *
     * @param projectId Id of project
     */
    canDeleteProject( projectId: number ): Observable<string> {
        return this.backendService.getData( `api/v1/fs/res/canDeleteProject/${projectId}` );
    }

    /**
     * get projects by test from database
     *
     * @param searchText contained in project
     */
    getProjects( searchText?: string, from?: string, until?: string, limit?: number, offset?: number ): Observable<Project[]> {
        if(from || until) {
            let isLimit = limit !== null && limit !== undefined ? '&l=' + limit : '';
            let isOffset = offset ? '&offset=' + offset : '';
            let isUntil = until ? '&until=' + until : '';
            let isFrom = from ? '&from=' + from : '';
            let isSearchText = searchText ? 'q='+ searchText : '';
            let urlQueryTemp = 'api/v1/fs/res/project?' + isSearchText + isFrom + isUntil + isLimit + isOffset;
            let urlQuery =  urlQueryTemp.replace('?&', '?');
            return this.backendService.getData(urlQuery);
        } else {
            return this.backendService.getData( `api/v1/fs/res/project?q=${encodeURI(searchText)}` );
        }
    }

    /**
     * get projects by test from database
     *
     * @param searchText contained in project
     */
    getSimpleProjects( searchText: string ): Observable<SimpleProject[]> {
        return this.backendService.getData( `api/v1/fs/res/project/simple?q=${encodeURI(searchText)}` );
    }

    /**
     * get projects by ids from database
     *
     * @param ids a set of project ids
     */
    getProjectsByIds( ids: Set<number> ): Observable<Project[]> {
        let idsStr = '';
        if ( ids.size > 0 ) {
            for ( let id of ids ) {
                idsStr += id + ',';
            }
            idsStr = idsStr.substring( 0, idsStr.length - 1 );
        }
        return this.backendService.getData( `api/v1/fs/res/projects/${idsStr}` );
    }

    /**
     * get dependent projects by
     *
     * @param projectId id of project
     */
    getDependentProjects( projectId: number ): Observable<Project[]> {
        return this.backendService.getData( `api/v1/fs/res/dependentProjects/${projectId}` );
    }

    /**
     * let the backend verify the rights if the auth can see/edit project.financedata
     * (need to be in BE because we cant check rightsparameter in FE)
     *
     * @param projectId Id of project.
     * @return Observable.of( comma separated string of granted authorities )
     */
    hasRightsForFinanceData( projectId: number ): Observable<string> {
        return this.backendService.getData( `api/v1/fs/res/project/hasRightsForFinanceData/${projectId}` );
    }

    /**
     * let the backend verify the rights if the auth can see/edit project.commentary
     * (need to be in BE because we cant check rightsparameter in FE)
     *
     * @param projectId Id of project.
     * @return Observable.of( 0 = none; 1 = read; 2 = write )
     */
    hasRightsForCommentary( projectId: number ): Observable<number> {
        return this.backendService.getData( `api/v1/fs/res/project/hasRightsForComment/${projectId}` );
    }

    /**
     * let the backend verify the rights if the auth can see/edit project.uniquename
     * (need to be in BE because we cant check rightsparameter in FE)
     *
     * @param projectId Id of project.
     * @return Observable.of( 0 = none; 1 = read/write )
     */
    hasRightsForUniquename( projectId: number ): Observable<number> {
        return this.backendService.getData( `api/v1/fs/res/project/hasRightsForUniquename/${projectId}` );
    }

    canEditProject( id: number ): Observable<boolean> {
        return this.backendService.postData(
            `api/v1/fs/res/hasRightsOnObject/ProjectObj/${id}/true`,
            ['RIGHT_FS_RES_PROJECT_EDIT_PROJECT', 'RIGHT_FS_RES_PROJECT_EDIT_PROJECT_OWN']
        );
    }
}
